import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Gavel, Check } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

export default function JoinInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then((r) => { setInvite(r.data.invitation); setMeeting(r.data.meeting); })
      .catch(() => toast.error("Invalid invitation"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setJoining(true);
    try {
      await api.post(`/invitations/${token}/accept`);
      toast.success("You're on the board.");
      setInvite((prev) => ({ ...prev, status: "accepted" }));
      // If host's session is currently active, jump straight into the live room.
      if (meeting?.id) {
        if (meeting.status === "active") nav(`/live/${meeting.id}`);
        else nav(`/meetings/${meeting.id}/minutes`);
      }
    } catch {
      toast.error("Could not accept invitation");
    } finally {
      setJoining(false);
    }
  };

  const goLive = () => meeting?.id && nav(`/live/${meeting.id}`);
  const goMinutes = () => meeting?.id && nav(`/meetings/${meeting.id}/minutes`);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#666]">Loading invitation…</div>;
  }
  if (!invite) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <p className="font-serif text-3xl">Invitation not found.</p>
        <Link to="/" className="mt-6 text-[#c89f45] text-sm">← Back to Boardroom</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0a0a0a]" data-testid="invite-root">
      <div className="max-w-lg w-full surface rounded-md p-10 text-center grain-overlay">
        <div className="h-12 w-12 mx-auto rounded-md border border-[#c89f45]/40 bg-[#121212] flex items-center justify-center mb-6">
          <Gavel size={20} className="text-[#c89f45]" />
        </div>
        <p className="label-eyebrow">Board invitation</p>
        <h1 className="font-serif text-3xl sm:text-4xl mt-4">
          You’ve been invited to a {meeting?.cadence || "weekly"} accountability board.
        </h1>
        {meeting?.title && (
          <p className="font-mono text-xs text-[#888] mt-4">Session: {meeting.title} · {meeting.status}</p>
        )}
        <p className="text-sm text-[#888] mt-6">
          Accepting the invitation lets you sit on this session — read its minutes, join live if it's in progress, and continue running your own boardroom on this device.
        </p>

        {invite.status === "accepted" ? (
          <div className="mt-8 flex flex-col items-center gap-4" data-testid="invite-accepted">
            <div className="flex items-center gap-2 text-[#c89f45]">
              <Check size={16} /> <span className="text-sm">Invitation accepted</span>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              {meeting?.status === "active" && (
                <Button onClick={goLive} className="bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md" data-testid="invite-go-live">
                  Join live now
                </Button>
              )}
              <Button onClick={goMinutes} variant="outline" className="bg-transparent border-[#2a2a2a] text-[#fafafa] hover:bg-[#1a1a1a] hover:text-white rounded-md" data-testid="invite-go-minutes">
                View minutes
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={accept}
            disabled={joining}
            className="mt-8 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-11 px-6"
            data-testid="accept-invite-button"
          >
            {joining ? "Joining…" : "Accept invitation"}
          </Button>
        )}
      </div>
    </div>
  );
}
